import React, { useEffect, useMemo, useState } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { Title, useNotify, usePermissions, useStore } from "react-admin";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { EventDashboardGridList } from "./EventDashboardGridList";
import { EventDashboardNamaazStats } from "./EventDashboardNamaazStats";
import { EventRelayAccessSyncButton } from "./EventRelayAccessSyncDialog";
import { callApi } from "@/dataprovider/misc-apis";
import { useRouteId } from "@/utils/route-utility";
import { isAsharaEvent } from "@/utils";
import { hasPermission } from "@/utils/permission-utils";
import { formatVenueLabel, getVenueTabCodes } from "@/utils/event-venue-utils";
import {
  isEventStatsPayload,
  type CurrentEvent,
  type DayWiseReceiptReportRow,
  type EventStatsNamaazRow,
  type EventStatsNiyaazRow,
} from "@/containers/events/types";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export const EventDashboard = () => {
  const notify = useNotify();
  const { permissions } = usePermissions();

  const [niyaazCounts, setNiyaazCounts] = useState<EventStatsNiyaazRow[]>([]);
  const [namaazCounts, setNamaazCounts] = useState<EventStatsNamaazRow[]>([]);
  const [receiptReport, setReceiptReport] = useState<DayWiseReceiptReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const eventId = useRouteId();
  const [currentEvent] = useStore<CurrentEvent | null>("currentEvent");

  const jamanTabCodes = useMemo(() => getVenueTabCodes(currentEvent, "JAMAN"), [currentEvent]);
  const namaazTabCodes = useMemo(() => getVenueTabCodes(currentEvent, "NAMAAZ"), [currentEvent]);

  const [value, setValue] = useState<string>("");
  const [namaazValue, setNamaazValue] = useState<string>("");

  useEffect(() => {
    if (jamanTabCodes.length && !jamanTabCodes.includes(value)) {
      setValue(jamanTabCodes[0]);
    }
  }, [jamanTabCodes, value]);

  useEffect(() => {
    if (namaazTabCodes.length && !namaazTabCodes.includes(namaazValue)) {
      setNamaazValue(namaazTabCodes[0]);
    }
  }, [namaazTabCodes, namaazValue]);

  const handleChange = (_e: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const handleNamaazVenueChange = (_e: React.SyntheticEvent, newValue: string) => {
    setNamaazValue(newValue);
  };

  const isAshara = isAsharaEvent(currentEvent ?? undefined);
  const canPreviewRelaySync =
    hasPermission(permissions, "relayAccessRequests.view") ||
    hasPermission(permissions, "relayAccessRequests.create");
  const canSyncRelayAccess = hasPermission(permissions, "relayAccessRequests.create");
  const relayEventLabel = currentEvent?.relayEvent?.name ?? undefined;

  const getStats = async () => {
    setLoading(true);
    try {
      const { data } = await callApi({
        location: "stats",
        headers: { eventId: String(eventId) },
        method: "GET",
      });
      if (isEventStatsPayload(data)) {
        setNiyaazCounts(data.niyaazCounts);
        setNamaazCounts(data.namaazVenueCounts);
        setReceiptReport(data.dayWiseReceiptReport);
      }
    } catch {
      notify("Error loading data", { type: "warning" });
    }
    setLoading(false);
  };

  useEffect(() => {
    void getStats();
  }, []);

  return (
    <>
      <Title title={currentEvent?.name} />

      <Box sx={{ width: "100%", minWidth: 0, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={1.5}
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.5rem" } }}>
            {isAshara ? "Venue Stats" : "Jaman Venue Stats"}
          </Typography>
          {isAshara && currentEvent?.relayEventId ? (
            <EventRelayAccessSyncButton
              eventId={String(eventId)}
              relayEventId={currentEvent.relayEventId}
              relayEventLabel={relayEventLabel}
              canPreview={canPreviewRelaySync}
              canSync={canSyncRelayAccess}
            />
          ) : null}
        </Stack>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Markaz Tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
            }}
          >
            {jamanTabCodes.map((markaz) => (
              <Tab
                label={formatVenueLabel(markaz, currentEvent)}
                {...a11yProps(0)}
                key={markaz}
                value={markaz}
              />
            ))}
          </Tabs>
        </Box>

        <EventDashboardGridList
          niyaazCounts={niyaazCounts}
          receiptReport={receiptReport}
          isLoading={loading}
          selectedMarkaz={value}
        />
      </Box>
      {!isAshara && (
        <>
          <Divider />
          <Box sx={{ width: "100%", minWidth: 0, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
            <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: "1.15rem", sm: "1.5rem" } }}>
              Namaaz Venue Stats
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={namaazValue}
                onChange={handleNamaazVenueChange}
                aria-label="Markaz Tabs"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
                }}
              >
                {namaazTabCodes.map((namaazVenue) => (
                  <Tab
                    label={formatVenueLabel(namaazVenue, currentEvent)}
                    {...a11yProps(0)}
                    key={namaazVenue}
                    value={namaazVenue}
                  />
                ))}
              </Tabs>
            </Box>
            <EventDashboardNamaazStats namaazCounts={namaazCounts} selectedMarkaz={namaazValue} />
          </Box>
        </>
      )}
    </>
  );
};
